// IMPORTANT: currently, history only supports Collection's items and Collection's allocation dimension values
import "rxjs/add/operator/map";
import { List, Map, fromJS } from "immutable";

import { MUTATE_OPERATION_ENHANCED, COLLECTIONS } from "constants/action-types";
import {
    ROOT_NAMESPACE,
    ITEMS_NAMESPACE,
    ALLOCATION_DIMENSIONS_NAMESPACE,
    ALLOCATION_BAG_NAMESPACE,
} from "constants/product-collection";
import { collectionHistoryUpdate, collectionHistoryPush } from "actions/collections";
import { mutateOperation, IOperation, IOperationGroup, OperationType, MutableType } from "actions/mutate";
import { adjustDestinationIndex } from "helpers/collection";
import { sortRemoveOperations } from "helpers/mutable";
import { getAllocationDimensionById } from "reducers/selectors/collection";
import { Observable } from "rxjs";
import { HistoryEntry } from "reducers/collection";

enum HistoryAction {
    Undo = "undo",
    Redo = "redo",
}

export const historyPushEpic = (action$, store$) => {
    let buffers = [];
    return (
        action$
            .ofType(MUTATE_OPERATION_ENHANCED)
            .filter(
                ({
                    payload: {
                        HEAD: { namespace, object_type },
                    },
                    meta: { history = true } = {},
                }) => {
                    return (
                        namespace === ROOT_NAMESPACE &&
                        history === true &&
                        // only process history for item-level objects (not title edit, column visibility, saved views, etc.) and allocation dimensions
                        [ITEMS_NAMESPACE, ALLOCATION_DIMENSIONS_NAMESPACE, ALLOCATION_BAG_NAMESPACE].includes(
                            object_type
                        )
                    );
                }
            )
            .map(({ payload }) => {
                // We don't want to store reference to `snapshot` and `oldSnapshot` into history. They aren't needed and can cause memory-leak
                const cleanedPayload = {
                    ...payload,
                };

                const isPerformanceOptimizationOn = store$.getState().ldFlags.assortmentPerformance;
                if (isPerformanceOptimizationOn) {
                    delete cleanedPayload.snapshot;
                    delete cleanedPayload.oldSnapshot;
                }

                buffers.push(cleanedPayload);
                return cleanedPayload;
            })
            // only consider collection actions that didn't originate from this file
            // buffer mutations on every interval if buffering enabled
            .debounce(() => Observable.interval(50))
            .flatMap(() => {
                // clear for next batch
                const savedBuffers = buffers;
                buffers = [];
                const groupIdMap = {};
                const groups = savedBuffers.reduce((acc, operationsGroup) => {
                    const historyGroupId = operationsGroup.historyGroupId ?? "";
                    if (groupIdMap[historyGroupId] !== undefined) {
                        acc[groupIdMap[historyGroupId]].push(operationsGroup);
                    } else if (groupIdMap[historyGroupId] === undefined) {
                        acc.push([operationsGroup]);
                        groupIdMap[historyGroupId] = acc.length - 1;
                    }
                    return acc;
                }, []);
                return groups.map((group) => collectionHistoryPush(group));
            })
    );
};

export const historyUndoEpic = (action$, store) => {
    return action$
        .ofType(COLLECTIONS.COLLECTION.HISTORY.UNDO)
        .map(({ payload }) => {
            const state = store.getState();
            const { collection, mutate } = state;
            return { payload, collection, mutate, state, type: HistoryAction.Undo };
        })
        .map(extractHistoryData)
        .flatMap(({ historyDirections, oldUndo, oldRedo }) => {
            if (historyDirections.length === 0) {
                // use flatMap to remove this row, since there's no history or no items to operate on
                return [];
            }

            const undo = oldUndo.slice(0, -1);
            const redo = oldRedo.concat(oldUndo.slice(-1));

            return [{ redo, undo, historyDirections, mutateState: store.getState().mutate }];
        })
        .flatMap(handleHistoryChange);
};

export const historyRedoEpic = (action$, store) => {
    return action$
        .ofType(COLLECTIONS.COLLECTION.HISTORY.REDO)
        .map(({ payload }) => {
            const state = store.getState();
            const { collection, mutate } = state;
            return { payload, collection, mutate, state, type: HistoryAction.Redo };
        })
        .map(extractHistoryData)
        .flatMap(({ historyDirections, oldUndo, oldRedo }) => {
            if (historyDirections.length === 0) {
                // use flatMap to remove this row, since there's no history or no items to operate on
                return [];
            }

            const redo = oldRedo.slice(0, -1);
            const undo = oldUndo.concat(oldRedo.slice(-1));

            return [{ redo, undo, historyDirections, mutateState: store.getState().mutate }];
        })
        .flatMap(handleHistoryChange);
};

interface IHistoryDirection {
    items: List<any>;
    operationGroup: IOperationGroup;
    operations: IOperation[];
}
// extracting basic history variables happens the same way for both undo and redo
const extractHistoryData = ({ payload: { collectionId }, type, mutate, collection, state }) => {
    const { redo: oldRedo, undo: oldUndo } = collection;
    const collectionState = mutate.snapshots && mutate.snapshots.get(collectionId);

    const historyDirections: IHistoryDirection[] = [];
    const history = type === HistoryAction.Undo ? oldUndo[oldUndo.length - 1] : oldRedo[oldRedo.length - 1];
    if (history) {
        // Reverse so it's LIFO (Last-in, first-out)
        history.reverse().forEach((historyEntry) => {
            const historyDirection = type === HistoryAction.Undo ? historyEntry.down : historyEntry.up;
            const directionEntry = {
                items: List(),
                operationGroup: historyDirection,
                // reverse() is required for undo operations
                operations: sortRemoveOperations(
                    type === HistoryAction.Undo ? historyDirection.operations.reverse() : historyDirection.operations
                ),
            };
            const objectId = historyEntry.objectId;
            // Object type isn't available as action payload, we need to get from HEADs.
            const objectType = mutate.HEADs && mutate.HEADs.get(objectId).object_type;

            if (objectType === ITEMS_NAMESPACE) {
                directionEntry.items = mutate.snapshots && collectionState && mutate.snapshots.get(objectId);
            } else if (objectType === ALLOCATION_DIMENSIONS_NAMESPACE) {
                const allocationDimension = getAllocationDimensionById(state, objectId);
                if (allocationDimension) {
                    directionEntry.items = fromJS(allocationDimension.dimension_values);
                }
            } else if (objectType === ALLOCATION_BAG_NAMESPACE) {
                directionEntry.items = mutate.snapshots && collectionState && mutate.snapshots.get(objectId);
            }
            historyDirections.push(directionEntry);
        });
    }
    return { historyDirections, oldUndo, oldRedo };
};

// TODO: New Bag. Add unit test for mutations grouping
interface IHandleHistoryChange {
    redo: HistoryEntry[];
    undo: HistoryEntry[];
    historyDirections: IHistoryDirection[];
    mutateState: any;
}
// history changes work exactly the same when undoing vs redoing, save for a few details which are abstracted away
const handleHistoryChange = ({ redo, undo, historyDirections, mutateState }: IHandleHistoryChange) => {
    const actions: object[] = [collectionHistoryUpdate(undo, redo)];
    historyDirections.forEach((historyDirection) => {
        const { items, operations, operationGroup } = historyDirection;
        const totalLength = items.size;
        // Object type isn't available as action payload, we need to get from HEADs.
        const objectType = mutateState.HEADs && mutateState.HEADs.get(operationGroup.objectId).object_type;

        const adjustedOperations = operations
            .map((operation: IOperation) => {
                const currentIndex = getCurrentIndex(items, operation, objectType, operationGroup.mutableType);
                return { currentIndex, operation };
            })
            // filter out operations that try to move non-existent rows--otherwise, things break
            .filter(({ currentIndex, operation }: { currentIndex: number; operation: IOperation }) => {
                // For non-map object, we will need index adjustment
                if (operationGroup.mutableType !== MutableType.Map) {
                    if ([OperationType.Move, OperationType.Remove, OperationType.Set].includes(operation.operation)) {
                        return currentIndex >= 0;
                    } else if (operation.operation === OperationType.Insert) {
                        return currentIndex < 0;
                    }
                }

                return true;
            })
            // transform the operation
            // (the state may have changed since the operation was pushed onto the history stack)
            .map(({ operation, currentIndex }: { operation: IOperation; currentIndex: number }) => {
                let adjustedOperation = {
                    ...operation,
                };

                // Only do index adjustment if this is non-Map object
                if (operationGroup.mutableType !== MutableType.Map) {
                    // since it's possible that some other mutation has moved/removed a row,
                    // let's modify the correct index instead of the stale one
                    // if someone removed our row (currentIndex < 0) then default to the original index
                    const newIndex = currentIndex >= 0 ? currentIndex : operation.index;
                    let adjustedDestination = operation.destination_index;
                    // adjust any operation indices to work with OT mutations
                    // (moving a row--remove then insert--requires modification to the destination index in certain cases)
                    adjustedDestination = adjustDestinationIndex({
                        source: operation.index,
                        proposedDestination: operation.destination_index,
                        totalLength,
                    });
                    adjustedOperation = {
                        ...adjustedOperation,
                        index: newIndex,
                        destination_index: adjustedDestination,
                    };
                }

                return adjustedOperation;
            });

        if (adjustedOperations.length > 0) {
            actions.push(
                mutateOperation(operationGroup.mutableType, operationGroup.objectId, adjustedOperations, {
                    history: false,
                })
            );
        }
    });

    return actions;
};

const getCurrentIndex = (
    items: List<Map<string, object>>,
    operation: IOperation,
    objectType: string,
    mutableType: MutableType
) => {
    // Map doesn't care about positional index
    if (mutableType === MutableType.Map) {
        return undefined;
    }

    return items.findIndex((item) => {
        if (objectType === ALLOCATION_DIMENSIONS_NAMESPACE) {
            return item.getIn(["id"]) === operation.rowId;
        } else {
            // Default to get index of Collection's items
            return item.getIn(["fields", "id", "string_value"]) === operation.rowId;
        }
    });
};
